import React, { useState } from 'react';
import { makeStyles, Menu, MenuItem, Typography, Box } from '@material-ui/core';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: 8,
    minWidth: 80,
    justifyContent: 'flex-end',
  },
  valueText: {
    color: '#fff',
    fontSize: '16px',
    fontWeight: 400,
  },
  icon: {
    color: '#fff',
    fontSize: '20px',
    transition: 'transform 0.2s',
  },
  iconOpen: {
    transform: 'rotate(180deg)',
  },
  menu: {
    '& .MuiPaper-root': {
      backgroundColor: '#513E52',
      color: '#fff',
      minWidth: 120,
      borderRadius: 8,
      marginTop: 4,
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
    },
  },
  menuItem: {
    fontSize: '14px',
    padding: '10px 16px',
    '&:hover': {
      backgroundColor: '#FFFFFF2E',
    },
    '&.Mui-selected': {
      backgroundColor: '#FFFFFF2E',
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#FFFFFF2E',
    }
  },
}));

export interface FanSpeedOption {
  id: number;
  label: string | React.ReactNode;
}

export interface FanSpeedPickerProps {
  value: number;
  options: FanSpeedOption[];
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
}

const FanSpeedPicker: React.FC<FanSpeedPickerProps> = ({
  value,
  options,
  onChange,
  disabled = false,
  className,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (optionId: number) => {
    onChange(optionId);
    handleClose();
  };

  const selectedOption = options.find(opt => opt.id === value);

  return (
    <>
      <Box
        className={clsx(classes.container, className)}
        onClick={handleClick}
        style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
      >
        <Typography className={classes.valueText}>
          {selectedOption ? selectedOption.label : ''}
        </Typography>
        <svg
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={clsx(classes.icon, Boolean(anchorEl) && classes.iconOpen)}
        >
          <path d="M5.91304 6.94138C5.77971 6.94138 5.65471 6.91638 5.53804 6.86638C5.42138 6.81638 5.31304 6.74138 5.21304 6.64138L0.263043 1.69138C0.0797099 1.50805 -0.00778985 1.27471 0.000543479 0.991379C0.00887681 0.708046 0.10471 0.474712 0.288043 0.291379C0.50471 0.0747121 0.74221 -0.0211207 1.00054 0.00387933C1.25888 0.0288793 1.48804 0.133046 1.68804 0.316379L5.91304 4.54138L10.138 0.316379C10.3214 0.133046 10.5589 0.0330458 10.8505 0.0163791C11.1422 -0.000287531 11.3797 0.0997127 11.563 0.316379C11.7797 0.499713 11.8755 0.728879 11.8505 1.00388C11.8255 1.27888 11.7214 1.51638 11.538 1.71638L6.61304 6.64138C6.51304 6.74138 6.40471 6.81638 6.28804 6.86638C6.17138 6.91638 6.04638 6.94138 5.91304 6.94138Z" fill="#B1A3B1" />
        </svg>
      </Box>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className={classes.menu}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleSelect(option.id)}
            className={classes.menuItem}
            selected={option.id === value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default FanSpeedPicker;
